import React, { useEffect, useMemo } from 'react';
import Dropdown, { Option } from '@app/react-common/components/dropdown/Dropdown';
import { PercentInput } from '@app/react-common/components/percent-input/PercentInput';
import { TrafficAllocationVariant } from '@app/react-common/components/traffic-allocation/TrafficAllocationShared';
import { AbTestFormData } from '../../shared/abTestModalUtils';
import { CheckoutProfile } from '../../hooks/useCheckoutProfiles';
import {
  CHECKOUT_PROFILE_SLOTS,
  CONTROL_SLOT,
  DEFAULT_CHECKOUT_PROFILE_PERCENTAGE,
  TEST_SLOT,
} from './checkoutProfileVariants';

interface CheckoutProfileAllocationSectionProps {
  formData: AbTestFormData;
  setFormData: React.Dispatch<React.SetStateAction<AbTestFormData>>;
  setTrafficAllocationValid: (valid: boolean) => void;
  mode: string;
  checkoutProfiles: CheckoutProfile[];
  isLoading?: boolean;
}

const toOption = (variant: TrafficAllocationVariant, checkoutProfiles: CheckoutProfile[]): Option => ({
  label: variant.name,
  value: variant.widgetExternalId,
  disabled: !checkoutProfiles.some((profile) => Number(profile.id) === Number(variant.widgetExternalId)),
});

export function CheckoutProfileAllocationSection({
  formData,
  setFormData,
  setTrafficAllocationValid,
  mode,
  checkoutProfiles,
  isLoading = false,
}: CheckoutProfileAllocationSectionProps) {
  const variants = useMemo(() => formData.abTestVariants ?? [], [formData.abTestVariants]);
  const viewMode = mode === 'view';

  useEffect(() => {
    const selectedVariants = variants.filter((variant) => variant.isSelected);
    const percentageSum = selectedVariants.reduce((total, variant) => total + variant.percentageTraffic, 0);
    setTrafficAllocationValid(selectedVariants.length === CHECKOUT_PROFILE_SLOTS.length && percentageSum === 100);
  }, [variants, setTrafficAllocationValid]);

  const options = useMemo(
    () => variants.map((variant) => toOption(variant, checkoutProfiles)),
    [variants, checkoutProfiles],
  );

  const selectVariantForSlot = (slotIndex: number, option: Option | null) => {
    setFormData((prevData) => {
      const currentVariant = prevData.abTestVariants.find((variant) => variant.slotIndex === slotIndex);
      const percentageTraffic = currentVariant?.percentageTraffic || DEFAULT_CHECKOUT_PROFILE_PERCENTAGE;
      return {
        ...prevData,
        abTestVariants: prevData.abTestVariants.map((variant) => {
          if (option && variant.widgetExternalId === option.value) {
            return { ...variant, isSelected: true, slotIndex, percentageTraffic };
          }
          if (variant.slotIndex === slotIndex) {
            return { ...variant, isSelected: false, slotIndex: undefined, percentageTraffic: 0 };
          }
          return variant;
        }),
      };
    });
  };

  const setSlotPercentage = (slotIndex: number, percentage: number) => {
    const otherSlotIndex = slotIndex === CONTROL_SLOT ? TEST_SLOT : CONTROL_SLOT;
    setFormData((prevData) => ({
      ...prevData,
      abTestVariants: prevData.abTestVariants.map((variant) => {
        if (variant.slotIndex === slotIndex) return { ...variant, percentageTraffic: percentage };
        if (variant.slotIndex === otherSlotIndex) return { ...variant, percentageTraffic: 100 - percentage };
        return variant;
      }),
    }));
  };

  if (isLoading) return null;

  return (
    <div className="checkout-profile-allocation">
      {CHECKOUT_PROFILE_SLOTS.map(({ index, label }) => {
        const selectedVariant = variants.find((variant) => variant.slotIndex === index);
        const otherSlotProfileId = variants.find(
          (variant) => variant.isSelected && variant.slotIndex !== index,
        )?.widgetExternalId;

        return (
          <div key={index} className="checkout-profile-allocation__slot">
            <label className="checkout-profile-allocation__label body-14-medium">{label}</label>
            <div className="checkout-profile-allocation__row">
              <div className="checkout-profile-allocation__dropdown">
                <Dropdown
                  options={options.filter((option) => option.value !== otherSlotProfileId)}
                  placeholder="Select a checkout profile"
                  preSelectedOption={selectedVariant ? toOption(selectedVariant, checkoutProfiles) : null}
                  onSelect={(option: Option | null) => selectVariantForSlot(index, option)}
                  disabled={viewMode}
                />
              </div>
              <div className="checkout-profile-allocation__percentage">
                <PercentInput
                  value={selectedVariant?.percentageTraffic ?? 0}
                  setValue={(value: number) => setSlotPercentage(index, value)}
                  setValid={() => {}}
                  disabled={viewMode || !selectedVariant}
                  viewMode={viewMode}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
